import React, { useCallback, useMemo, useSyncExternalStore } from "react";
import type { AgentUiNode, AgentUiSnapshot } from "src/components/views/ai/assistant/uiContext/agentUiNodeTypes.ts";
import { useContextOrThrow } from "src/utils/useContextOrThrow.tsx";

export const ROOT_AGENT_UI_SCOPE_ID = "root";

export interface RegisteredAgentUiNode {
	id: string;
	parentId: string;
	order: number;
	getNode: () => AgentUiNode;
}

export interface AgentUiRegistryContextValue {
	registerNode: (node: Omit<RegisteredAgentUiNode, "order">) => () => void;
	notifyNodeChanged: () => void;
	createSnapshot: () => AgentUiSnapshot;
	createTitlePath: () => string[];
	getRevision: () => number;
	subscribe: (listener: () => void) => () => void;
}

export const AgentUiRegistryContext = React.createContext<AgentUiRegistryContextValue | undefined>(undefined);
export const AgentUiScopeIdContext = React.createContext<string>(ROOT_AGENT_UI_SCOPE_ID);
export const AgentUiRegistrationEnabledContext = React.createContext(true);

export function useAgentUiSnapshot(): () => AgentUiSnapshot {
	const registry = useContextOrThrow(AgentUiRegistryContext);
	return useCallback(() => {
		return registry.createSnapshot();
	}, [registry]);
}

export function useAgentUiTitlePath(): string[] {
	const registry = useContextOrThrow(AgentUiRegistryContext);
	const revision = useSyncExternalStore(registry.subscribe, registry.getRevision, registry.getRevision);
	return useMemo(() => {
		return registry.createTitlePath();
	}, [registry, revision]);
}

export function useCurrentAgentUiScopeId(): string {
	return React.useContext(AgentUiScopeIdContext);
}
